import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "cn";

import { TurnUsageFooter } from "../../usage/TurnUsageFooter.tsx";
import type { AgentMessage, ConfirmationDecision } from "../agent-event.ts";
import {
  callResolution,
  cardOutcome,
  type DecisionState,
} from "../decisions.ts";
import { PresentationPanel } from "../presentation/PresentationPanel.tsx";
import { messageAnchor, MessageIdProvider } from "../surface.tsx";
import { Confirmation } from "./Confirmation.tsx";
import { GivenAnswers } from "./GivenAnswers.tsx";
import { Markdown } from "./Markdown.tsx";
import { Questions, type GivenAnswer } from "./Questions.tsx";
import { Reasoning } from "./Reasoning.tsx";
import { UiComponent } from "./registry.tsx";
import { ResourceLink } from "./ResourceLink.tsx";
import { ToolCall } from "./ToolCall.tsx";

export function Message({
  message,
  decisionState,
  onResolveConfirmation,
  onAnswerQuestions,
  onFillComposer,
  busy,
}: {
  message: AgentMessage;
  /**
   * Qué se ha contestado, cómo se decidió y cómo acabó cada tarjeta.
   *
   * Viene de arriba y no de un estado de la tarjeta: la tarjeta se desmonta al
   * cerrar la ventana del chat o al cambiar de modo, y al volver ofrecería
   * otra vez unos botones que ya no valen. Y cómo acabó llega en OTRO
   * mensaje --el de la decisión--, que esta no ve.
   */
  decisionState: DecisionState;
  onResolveConfirmation: (
    actionId: string,
    decision: ConfirmationDecision,
  ) => void;
  onAnswerQuestions: (
    questionId: string,
    answers: readonly GivenAnswer[],
    note?: string,
  ) => void;
  /** Rellena el compositor con una pregunta lista, sin enviarla (Parte 0). */
  onFillComposer: (text: string) => void;
  busy: boolean;
}) {
  const { answered } = decisionState;

  /**
   * Un mensaje sin nada que enseñar no se dibuja: es el de aprobar o
   * contestar, cuyo texto era para el modelo (1.7) o iba vacío. Una etiqueta
   * "Tú" encima de una caja vacía se lee como un fallo.
   */
  if (message.parts.length === 0 && message.usage === undefined) return null;

  return (
    // El ancla es lo que permite saltar a ESTE mensaje desde la ventana
    // pequeña cuando algo no cabía allí.
    <MessageIdProvider messageId={message.id}>
      <article id={messageAnchor(message.id)} className="grid min-w-0 gap-1.5">
        <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
          {message.role === "user" ? "Tú" : "Asistente"}
        </div>

        {/* El cuerpo también tiene que poder encogerse, o una tabla ancha lo
          estira. Lo del usuario va en un recuadro; lo del asistente, suelto:
          es lo que separa una pregunta de una respuesta sin decirlo. */}
        <div
          className={cn(
            "min-w-0 [&>*+*]:mt-3",
            message.role === "user" &&
              "rounded-lg border bg-card px-3.5 py-2.5",
          )}
        >
          {message.parts.map((part, index) => {
            switch (part.type) {
              case "text":
                return <Markdown key={index} text={part.text} />;

              case "reasoning":
                return <Reasoning key={index} text={part.text} />;

              case "tool":
                return (
                  <ToolCall
                    key={part.callId}
                    part={part}
                    resolution={callResolution(
                      message,
                      part.callId,
                      decisionState,
                    )}
                  />
                );

              case "ui_component":
                return (
                  <UiComponent
                    key={index}
                    render={part.render}
                    props={part.props}
                  />
                );

              case "resource_link":
                return <ResourceLink key={index} resource={part.resource} />;

              case "file":
                return (
                  <div
                    key={index}
                    className="rounded-lg border bg-card px-3.5 py-2.5"
                  >
                    <strong className="font-semibold">{part.name}</strong>
                    <p className="text-sm text-muted-foreground">
                      {part.mimeType} · {Math.round(part.sizeBytes / 1024)} KB
                    </p>
                  </div>
                );

              case "confirmation_request":
                return (
                  <Confirmation
                    key={part.actionId}
                    part={part}
                    outcome={cardOutcome(part, decisionState)}
                    onResolve={onResolveConfirmation}
                    disabled={busy}
                  />
                );

              case "question_request":
                return (
                  <Questions
                    key={part.questionId}
                    part={part}
                    settled={
                      part.settled === true || answered.has(part.questionId)
                    }
                    onAnswer={onAnswerQuestions}
                    disabled={busy}
                  />
                );

              case "question_answer":
                return <GivenAnswers key={index} part={part} />;

              case "presentation":
                return (
                  <PresentationPanel
                    key={index}
                    presentation={part.presentation}
                    onAsk={onFillComposer}
                  />
                );

              case "error":
                return (
                  <Alert key={index} variant="destructive">
                    <AlertTitle>{part.code}</AlertTitle>
                    <AlertDescription>{part.message}</AlertDescription>
                  </Alert>
                );
            }
          })}
        </div>

        {message.usage !== undefined && (
          <TurnUsageFooter usage={message.usage} />
        )}
      </article>
    </MessageIdProvider>
  );
}
